import React, { useState } from "react";
import toast from "react-hot-toast";
import { useAppNavigate } from "../../hooks/useAppNavigate";
import { usePodData } from "../../hooks/usePodData";
import { useAppContext } from "../../context/AppContext";
import { useAuth } from "../../context/AuthContext";
import { useLocalization } from "../../context/LocalizationContext";
import { createGuest, startGuestSession, GuestApiError } from "../../apis/guest";
import { fetchPatientIdByUserId } from "../../apis/patient";
import type { GuestGender } from "../../dtos/guest";
import type { UserData } from "../../dtos/patient";
import Header from "../../containers/Header";
import OptionGroup from "../../components/shared/OptionGroup";
import LoadingScreen from "../../components/LoadingScreen";
import "./styles.css";

const MAX_USERNAME_RETRIES = 3;

const generateGuestUsername = () =>
  `guest${Math.floor(Math.random() * 1000).toString().padStart(3, "0")}`;

const GuestMode: React.FC = () => {
  const navigate = useAppNavigate();
  const { podId, podIP } = usePodData();
  const { persistSession } = useAuth();
  const { setUserData } = useAppContext();
  const { t } = useLocalization();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState(generateGuestUsername);
  const [gender, setGender] = useState<GuestGender | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);

  const genderOptions = [
    { value: "MALE", label: t("male") },
    { value: "FEMALE", label: t("female") },
  ];

  const isValid = username.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || submitting || !podId) return;

    setSubmitting(true);
    try {
      let currentUsername = username.trim();
      let guest;
      for (let attempt = 0; ; attempt++) {
        try {
          guest = await createGuest({
            first_name: firstName.trim() || undefined,
            last_name: lastName.trim() || undefined,
            username: currentUsername,
            gender,
          });
          break;
        } catch (err) {
          const isUsernameTaken = err instanceof GuestApiError && err.errorCode === "UM-098";
          if (!isUsernameTaken || attempt >= MAX_USERNAME_RETRIES) throw err;
          currentUsername = generateGuestUsername();
          setUsername(currentUsername);
        }
      }
      const userId = guest.user_id;

      const session = await startGuestSession({
        pod_id: podId,
        user_id: userId,
        username: currentUsername,
      });

      // Neither create-guest nor start-guest-session returns a patientId, and the
      // user id is not one — resolve the real patient id from /mt-vmr/patient/profile.
      const patientId = await fetchPatientIdByUserId(userId);

      const userData: UserData = {
        id: userId,
        patientId,
        sessionId: String(session.sessionId),
        podIP: session.podIp ?? podIP ?? "",
        name: guest.full_name,
        gender: guest.gender ?? "",
        dateOfBirth: null,
        age: null,
        email: guest.email ?? "",
        isGuest: true,
      };

      persistSession(userData);
      setUserData(userData);
      navigate("/dashboard");
    } catch (err) {
      if (err instanceof GuestApiError && err.errorCode === "MT-084") {
        toast.error(t("errors.podAlreadyInUse"));
      } else if (err instanceof GuestApiError && err.errorCode === "UM-098") {
        toast.error(t("errors.usernameTaken"));
      } else {
        toast.error(t("guestMode.error"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitting) {
    return <LoadingScreen />;
  }

  return (
    <div className="guest-mode-page page-container">
      <Header showBackButton="RIGHT" onBack={() => navigate(-1)} />

      <h1 className="page-title">{t("guestMode.title")}</h1>
      <p className="page-subtitle">{t("guestMode.subtitle")}</p>

      <form className="guest-mode-form" onSubmit={handleSubmit}>
        <div className="guest-mode-field">
          <label className="guest-mode-label" htmlFor="guest-first-name">
            {t("guestMode.firstName")}
          </label>
          <input
            id="guest-first-name"
            className="guest-mode-input"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder={t("guestMode.firstNamePlaceholder")}
          />
        </div>

        <div className="guest-mode-field">
          <label className="guest-mode-label" htmlFor="guest-last-name">
            {t("guestMode.lastName")}
          </label>
          <input
            id="guest-last-name"
            className="guest-mode-input"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder={t("guestMode.lastNamePlaceholder")}
          />
        </div>

        <div className="guest-mode-field">
          <label className="guest-mode-label" htmlFor="guest-username">
            {t("guestMode.username")}
          </label>
          <div className="guest-mode-username-row">
            <input
              id="guest-username"
              className="guest-mode-input"
              type="text"
              value={username}
              readOnly
              required
            />
            <button
              type="button"
              className="guest-mode-username-regenerate"
              onClick={() => setUsername(generateGuestUsername())}
              aria-label={t("guestMode.regenerateUsername")}
            >
              ↻
            </button>
          </div>
        </div>

        <OptionGroup
          title={t("guestMode.gender")}
          options={genderOptions}
          value={gender ?? ""}
          onChange={(value) => setGender(value as GuestGender)}
        />

        <button
          type="submit"
          className="guest-mode-submit"
          disabled={!isValid || submitting}
        >
          {t("guestMode.submit")}
        </button>
      </form>
    </div>
  );
};

export default GuestMode;
